import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { FormProvider, useForm } from 'react-hook-form';
import * as http from 'utils/http';
import { useAsync, useDebounce } from 'react-use';

import {
  createPointOfInterest,
  editAddressWithPOIdata,
  editPointOfInterest,
} from 'modules/point-of-interest/service';

import {
  findDuplicatedPOIs,
  parseToStreamsetsService,
  parseToStreamsetsServiceForCreation,
} from 'modules/point-of-interest/venue-detail-modal/util';

import VenueDuplicationAlert from '../VenueDuplicationAlert';

const VenueDetailContext = createContext({});

const VenueDetailContextProvider = ({ children, venueId, addressId, isCreating, closeModal }) => {
  const me = useSelector((state) => state.me);
  const [watchedValues, setWatchedValues] = useState(null);
  const [isCreatedConfirmation, setIsCreatedConfirmation] = useState(false);
  const [editMode, setEditMode] = useState({});
  const [duplicationAlertOpened, setDuplicationAlertOpened] = useState(true);
  const [idsOfDuplicatedPOIs, setIdsOfDuplicatedPOIs] = useState('');

  const userId = me?.id;

  const methods = useForm({
    mode: 'onChange',
  });

  const venueNameWatch = methods.watch('venueName');
  const latitudeWatch = methods.watch('position.lat');
  const longitudeWatch = methods.watch('position.lng');

  const handleEditClick = useCallback((fieldName) => {
    setEditMode((currentEditMode) => ({
      ...currentEditMode,
      [fieldName]: true,
    }));
  }, []);

  const handleDiscardClick = useCallback((fieldName) => {
    setEditMode((currentEditMode) => ({
      ...currentEditMode,
      [fieldName]: false,
    }));
  }, []);

  const onSubmit = useCallback(
    async (data) => {
      try {
        if (isCreating) {
          await createPointOfInterest(
            parseToStreamsetsServiceForCreation({ ...data, userId, addressId })
          );
          setIsCreatedConfirmation(true);
          return;
        }
        await editPointOfInterest(
          parseToStreamsetsService(venueId, { ...data, userId, addressId })
        );
      } catch (error) {
        console.error(await http.getError(error));
      } finally {
        setEditMode({});
      }
    },
    [venueId, isCreating, userId, addressId]
  );

  const handleSaveMatchingPOI = useCallback(
    async (matchingPOI) => {
      try {
        if (!addressId) {
          return;
        }
        await editAddressWithPOIdata(addressId, matchingPOI);
      } catch (error) {
        console.error(await http.getError(error));
      } finally {
        closeModal();
      }
    },
    [addressId, closeModal]
  );

  useDebounce(
    () => {
      setWatchedValues({
        venueNameWatch,
        latitudeWatch,
        longitudeWatch,
      });
    },
    1000,
    [venueNameWatch, latitudeWatch, longitudeWatch]
  );

  const { value: matchingPOIs } = useAsync(async () => {
    if (!isCreating) {
      return;
    }

    const {
      venueNameWatch: venueName,
      latitudeWatch: latitude,
      longitudeWatch: longitude,
    } = watchedValues || {};

    const duplicatedFound = await findDuplicatedPOIs({
      addressId,
      venueName,
      latitude,
      longitude,
    });

    if (!duplicatedFound) {
      return;
    }

    setIdsOfDuplicatedPOIs(duplicatedFound.map((poi) => poi.id).join(','));

    return duplicatedFound;
  }, [addressId, isCreating, watchedValues]);

  useEffect(() => {
    setDuplicationAlertOpened(true);
  }, [idsOfDuplicatedPOIs]);

  const value = {
    onEditClick: handleEditClick,
    onDiscardClick: handleDiscardClick,
    onSubmit: onSubmit,
    isCreating: isCreating,
    editMode,
    isCreatedConfirmation,
  };

  const shouldShowMatchingPOI = !!(matchingPOIs || []).length && duplicationAlertOpened;

  return (
    <VenueDetailContext.Provider value={value}>
      <>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} data-testid="poi-form">
            <>{children}</>
          </form>
        </FormProvider>
        {shouldShowMatchingPOI && (
          <VenueDuplicationAlert
            show
            onClose={() => setDuplicationAlertOpened(false)}
            duplicatedPOIs={matchingPOIs}
            onCancel={closeModal}
            onSave={handleSaveMatchingPOI}
          />
        )}
      </>
    </VenueDetailContext.Provider>
  );
};

const useVenueDetail = () => {
  return useContext(VenueDetailContext);
};

export { VenueDetailContextProvider, useVenueDetail };
